/**
 * Booking Calculations Library
 * Centralized pricing and VAT calculations for bookings
 */

import { PricingService } from "./pricing-service-unified";
import {
  BookingAddOn,
  BookingShopItem,
  BookingData,
  PricingBreakdown,
  BookingTotals,
  CustomerType,
  getCustomerType,
  getPriceType,
} from "./types";

// Re-export types for backward compatibility
export type {
  BookingAddOn,
  BookingShopItem,
  BookingData,
  PricingBreakdown,
  BookingTotals,
};

/**
 * Calculate complete booking totals including shows, add-ons, and shop items
 */
export function calculateBookingTotals(
  booking: BookingData,
  showUnitPrice: number,
  showVatRate: number,
  priceModifier?: {
    type: "discount" | "surcharge" | "percentage";
    value: number;
  },
): BookingTotals {
  const customerType = getCustomerType(booking.customer_type_id);
  const isCorporate = customerType === "corporate";
  const priceType = getPriceType(booking.price_type_id);

  // Calculate show pricing
  const showPricing = PricingService.calculate({
    unitPrice: showUnitPrice,
    vatPercentage: showVatRate,
    participants:
      priceType === "per_person" ? booking.participant_count || 1 : 1,
    customerType,
    priceType,
    priceModifier,
  });

  // Calculate add-ons
  const addOnsCalc = calculateAddOns(
    booking.booking_add_ons || [],
    customerType,
  );

  // Calculate shop items
  const shopItemsCalc = calculateShopItems(
    booking.booking_shop_items || [],
    customerType,
  );

  // Calculate totals using modifiedBase (after modifier applied)
  const subtotal =
    showPricing.modifiedBase + addOnsCalc.baseAmount + shopItemsCalc.baseAmount;
  const totalVat =
    showPricing.vatAmount + addOnsCalc.vatAmount + shopItemsCalc.vatAmount;
  const grandTotal = subtotal + totalVat;

  // Display values based on customer type
  const displayAmount = isCorporate ? subtotal : grandTotal;
  const displayLabel = isCorporate ? "excl. VAT" : "incl. VAT";

  // Build modifiers applied array
  const modifiersApplied = priceModifier
    ? [
        {
          name: priceModifier.type === "discount" ? "Discount" : "Surcharge",
          amount: showPricing.modifierAmount,
          type: priceModifier.type,
        },
      ]
    : undefined;

  return {
    shows: {
      baseAmount: showPricing.modifiedBase, // Use modified base for display
      vatAmount: showPricing.vatAmount,
      vatRate: showVatRate,
      total: showPricing.bookingTotal,
    },
    addOns: addOnsCalc,
    shopItems: shopItemsCalc,
    subtotal,
    totalVat,
    grandTotal,
    displayAmount,
    displayLabel,
    modifiersApplied,
  };
}

/**
 * Calculate add-ons pricing with VAT
 */
export function calculateAddOns(
  addOns: BookingAddOn[],
  customerType: CustomerType,
): PricingBreakdown {
  if (!addOns || addOns.length === 0) {
    return { baseAmount: 0, vatAmount: 0, vatRate: 0, total: 0 };
  }

  const calculations = addOns
    .filter((addon) => !addon.is_archived)
    .map((addon) => extractAddOnPricing(addon, customerType));

  const baseAmount = calculations.reduce(
    (sum, calc) => sum + calc.baseAmount,
    0,
  );
  const vatAmount = calculations.reduce((sum, calc) => sum + calc.vatAmount, 0);

  // Get average VAT rate if needed
  const avgVatRate =
    calculations.length > 0 && baseAmount > 0
      ? (vatAmount / baseAmount) * 100
      : 0;

  return {
    baseAmount,
    vatAmount,
    vatRate: Math.round(avgVatRate),
    total: baseAmount + vatAmount,
  };
}

/**
 * Extract pricing from a single add-on
 */
export function extractAddOnPricing(
  addon: BookingAddOn,
  customerType: CustomerType,
): PricingBreakdown {
  // Use new VAT fields if available
  if (
    addon.total_price_excl_vat !== undefined &&
    addon.vat_amount !== undefined
  ) {
    return {
      baseAmount: Number(addon.total_price_excl_vat) || 0,
      vatAmount: Number(addon.vat_amount) || 0,
      vatRate: Number(addon.vat_rate) || 0,
      total:
        (Number(addon.total_price_excl_vat) || 0) +
        (Number(addon.vat_amount) || 0),
    };
  }

  // Fallback to calculation from pricing data
  // Try 'pricings' first (correct), then 'pricing' (legacy)
  const pricingData = addon.add_ons?.pricings || addon.add_ons?.pricing;
  const vatRate = pricingData?.vat_percentage || addon.vatPercentage || 0;
  const unitPrice = pricingData?.price || addon.price || 0;

  const calc = PricingService.calculate({
    unitPrice,
    vatPercentage: vatRate,
    participants: 1,
    customerType,
    priceType: "fixed",
  });

  return {
    baseAmount: calc.baseAmount,
    vatAmount: calc.vatAmount,
    vatRate,
    total: calc.bookingTotal,
  };
}

/**
 * Calculate shop items pricing with VAT
 */
export function calculateShopItems(
  shopItems: BookingShopItem[],
  customerType: CustomerType,
): PricingBreakdown {
  if (!shopItems || shopItems.length === 0) {
    return { baseAmount: 0, vatAmount: 0, vatRate: 0, total: 0 };
  }

  const calculations = shopItems.map((item) => {
    // Try 'pricings' first (correct), then 'pricing' (legacy)
    const pricingData = item.shop_items?.pricings || item.shop_items?.pricing;
    const vatRate = pricingData?.vat_percentage || item.vatPercentage || 0;
    const itemPrice =
      item.total_price_excl_vat || pricingData?.price || item.price || 0;

    return PricingService.calculate({
      unitPrice: itemPrice,
      vatPercentage: vatRate,
      participants: 1,
      customerType,
      priceType: "fixed",
    });
  });

  const baseAmount = calculations.reduce(
    (sum, calc) => sum + calc.baseAmount,
    0,
  );
  const vatAmount = calculations.reduce((sum, calc) => sum + calc.vatAmount, 0);

  const avgVatRate = baseAmount > 0 ? (vatAmount / baseAmount) * 100 : 0;

  return {
    baseAmount,
    vatAmount,
    vatRate: Math.round(avgVatRate),
    total: baseAmount + vatAmount,
  };
}

/**
 * Format price for display
 */
export function formatPrice(amount: number, currency: string = "GBP"): string {
  return `${amount.toLocaleString()} ${currency}`;
}

/**
 * Get display total based on customer type
 */
export function getDisplayTotal(
  subtotal: number,
  vatAmount: number,
  isCorporate: boolean,
): { amount: number; label: string } {
  return {
    amount: isCorporate ? subtotal : subtotal + vatAmount,
    label: isCorporate ? "excl. VAT" : "incl. VAT",
  };
}
